import React, { useState, useEffect, useCallback } from 'react';
import { styled } from '@compiled/react';
import throttle from 'lodash/throttle';
import { useThemeObserver } from '@atlaskit/tokens';
import { ViewPage } from '@atlassian/embedded-confluence';
import { ExperienceTracker, ExperienceTrackerContext } from '@atlassian/experience-tracker';
import { PAGE_LAYOUT_OFFSET_TOP } from '@atlassian/jira-common-constants/src/page-layout.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import {
	USER_ID_TYPE,
	PARENT_PRODUCT,
} from '@atlassian/jira-servicedesk-knowledge-common/src/common/constants.tsx';
import { useArticleId } from '@atlassian/jira-servicedesk-knowledge-common/src/controllers/store/index.tsx';
import type { RefObject } from '@atlassian/jira-shared-types/src/general.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useRouter } from '@atlassian/react-resource-router';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { experienceTrackerCallback } from './utils/experience-tracker/index.tsx';
import { epViewLoad } from './utils/performance-analytics/index.tsx';

type BreadcrumbsRef = null | RefObject<HTMLDivElement>;

type Props = {
	spaceKey: string | undefined;
	pageId: string;
	breadcrumbsRef: BreadcrumbsRef;
	projectKey: string;
	spaceId: number | undefined;
	onIFrameLoaded: () => void;
};

const WINDOW_RESIZE_THROTTLE = 350;
const SHIM_URL = '/plugins/servlet/servicedesk/customer/confluence/shim/';

export const ConfluenceEmbeddedViewWrapper = ({
	spaceKey,
	pageId,
	breadcrumbsRef,
	projectKey,
	spaceId,
	onIFrameLoaded,
}: Props) => {
	const [experienceTracker] = useState(new ExperienceTracker());
	const cloudId = useCloudId();
	const { atlassianAccountId } = useTenantContext();
	const { data: projectContext } = useProjectContext();
	const projectId = projectContext?.projectId;
	const [breadcrumbsHeight, setBreadcrumbsHeight] = useState<number>(0);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [, routerActions] = useRouter();
	const [, { articleDeleteSuccess }] = useArticleId();
	const themeState = useThemeObserver();
	const { locale } = useIntl();

	const navigateToEmbeddedEditor = () => {
		if (spaceKey != null && projectKey != null) {
			const EDIT_ARTICLE_URL = `/jira/servicedesk/projects/${projectKey}/knowledge/space/${spaceKey}/articles/${pageId}/edit`;
			fireTrackAnalytics(createAnalyticsEvent({}), 'onEditClikedInEPView success', {
				articleId: pageId,
				spaceId,
				userId: atlassianAccountId,
				tenantId: cloudId,
			});
			routerActions.push(EDIT_ARTICLE_URL);
		}
	};

	const isCreatedInEmbeddedEditor = (url: string) => url.includes('edit-embed');

	// go/jfe-eslint
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedHandleResize = useCallback(
		throttle(() => {
			if (breadcrumbsRef?.current) {
				setBreadcrumbsHeight(breadcrumbsRef.current.clientHeight);
			}
		}, WINDOW_RESIZE_THROTTLE),
		[],
	);

	const handleViewPageLoadFailure = useCallback(() => {
		GlobalPageLoadExperience.markFMP();
		GlobalPageLoadExperience.failure({ force: true });
	}, []);

	useEffect(() => {
		epViewLoad.start();
		const viewSuccessCallback = () => {
			epViewLoad.stop();
			onIFrameLoaded();
		};
		const unsubscribe = experienceTracker.subscribe((payload) =>
			experienceTrackerCallback({
				payload,
				projectKey,
				pageId,
				routerActions,
				analyticsEvent: createAnalyticsEvent({}),
				deleteSuccessCallback: articleDeleteSuccess,
				viewSuccessCallback,
				onViewPageLoadFailure: handleViewPageLoadFailure,
			}),
		);

		return unsubscribe;
	}, [
		experienceTracker,
		pageId,
		routerActions,
		projectKey,
		createAnalyticsEvent,
		articleDeleteSuccess,
		onIFrameLoaded,
		handleViewPageLoadFailure,
	]);

	useEffect(() => {
		if (breadcrumbsRef?.current) {
			setBreadcrumbsHeight(breadcrumbsRef.current.clientHeight);
		}

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.addEventListener('resize', debouncedHandleResize);

		return () => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.removeEventListener('resize', debouncedHandleResize);
		};
	}, [debouncedHandleResize, breadcrumbsRef]);

	return (
		<StyledEmbeddedConfluenceContainer topOffset={breadcrumbsHeight}>
			<ExperienceTrackerContext.Provider value={experienceTracker}>
				<ViewPage
					locale={locale}
					spaceKey={spaceKey}
					contentId={pageId}
					allowedFeatures={[
						'byline-contributors',
						'sticky-header',
						'edit',
						'delete',
						'disable-share',
					]}
					parentProduct={PARENT_PRODUCT}
					navigationPolicy={{
						shimUrl: SHIM_URL,
						navigate: (url, modifiers, defaultNavigate) => {
							if (isCreatedInEmbeddedEditor(url)) {
								return navigateToEmbeddedEditor();
							}
							return defaultNavigate(url, modifiers);
						},
					}}
					parentProductContentContainerId={`${projectId}`}
					userInfo={{
						userId: atlassianAccountId || '',
						userIdType: USER_ID_TYPE,
					}}
					themeState={themeState}
				/>
			</ExperienceTrackerContext.Provider>
		</StyledEmbeddedConfluenceContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const StyledEmbeddedConfluenceContainer = styled.div<{ topOffset: number }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: `calc(100vh - ${PAGE_LAYOUT_OFFSET_TOP} - ${(props: { topOffset: number }) => props.topOffset}px)`,
});
